import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import type { Departamento } from "@/data/mock";
import { toast } from "sonner";
import { AlertCircle, Download, FileCheck2, Loader2, Upload } from "lucide-react";

type AcaoImportacao = "criar" | "atualizar" | "sem_alteracao" | "erro";

interface LinhaImportacao {
  linha: number;
  nome: string;
  email: string;
  acao: AcaoImportacao;
  alteracoes: string[];
  erros: string[];
  avisos: string[];
}

type ResolucaoDepartamento =
  | { acao: "criar" }
  | { acao: "vincular"; departamento: string; renomear: boolean };

interface DepartamentoNaoCadastrado {
  /** Nome normalizado — chave da resolução enviada à API. */
  chave: string;
  nome: string;
  linhas: number[];
  resolucao: ResolucaoDepartamento | null;
  erro: string | null;
}

interface ResultadoImportacao {
  confirmado: boolean;
  linhas: LinhaImportacao[];
  resumo: Record<AcaoImportacao, number>;
  departamentosNaoCadastrados: DepartamentoNaoCadastrado[];
  acoesDepartamento: {
    criar: string[];
    renomear: { de: string; para: string; usuarios: number }[];
  };
}

/** Escolha em andamento na tela; só vai para a API quando está completa. */
type Escolha =
  | { acao: "criar" }
  | { acao: "vincular"; departamento?: string; renomear?: boolean };

const completa = (e: Escolha | undefined): e is ResolucaoDepartamento =>
  !!e && (e.acao === "criar" || (!!e.departamento && e.renomear !== undefined));

const resolucoesCompletas = (escolhas: Record<string, Escolha>) =>
  Object.fromEntries(Object.entries(escolhas).filter(([, e]) => completa(e))) as Record<
    string,
    ResolucaoDepartamento
  >;

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ACAO: Record<AcaoImportacao, { rotulo: string; className: string }> = {
  erro: { rotulo: "Erro", className: "border-destructive/40 bg-destructive/10 text-destructive" },
  criar: { rotulo: "Novo", className: "border-green-600/30 bg-green-600/10 text-green-700 dark:text-green-400" },
  atualizar: { rotulo: "Atualizar", className: "border-blue-600/30 bg-blue-600/10 text-blue-700 dark:text-blue-400" },
  sem_alteracao: { rotulo: "Sem alteração", className: "text-muted-foreground" },
};

// Erros primeiro: é o que o RH precisa corrigir antes de confirmar.
const ORDEM: AcaoImportacao[] = ["erro", "atualizar", "criar", "sem_alteracao"];

/**
 * Importação em massa pela planilha modelo, em duas etapas: o arquivo é
 * validado e o RH vê a prévia linha a linha; só grava ao confirmar.
 */
export const ImportarUsuariosModal = ({ open, onClose, onSuccess }: Props) => {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<ResultadoImportacao | null>(null);
  const [validando, setValidando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [escolhas, setEscolhas] = useState<Record<string, Escolha>>({});

  useEffect(() => {
    if (!open) return;
    api
      .get<Departamento[]>("/departamentos")
      .then(setDepartamentos)
      .catch(() => toast.error("Erro ao carregar departamentos"));
  }, [open]);

  const handleClose = () => {
    if (importando) return;
    setArquivo(null);
    setPrevia(null);
    setEscolhas({});
    onClose();
  };

  const enviar = (f: File, confirmar: boolean, resolucoes: Record<string, ResolucaoDepartamento>) => {
    const fd = new FormData();
    fd.append("arquivo", f);
    fd.append("resolucoesDepartamento", JSON.stringify(resolucoes));
    return api.postForm<ResultadoImportacao>(
      `/colaboradores/importar${confirmar ? "?confirmar=true" : ""}`,
      fd,
    );
  };

  const validar = async (f: File) => {
    setArquivo(f);
    setPrevia(null);
    setEscolhas({});
    setValidando(true);
    try {
      setPrevia(await enviar(f, false, {}));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ler a planilha", { duration: 10_000 });
    } finally {
      setValidando(false);
    }
  };

  /** Revalida o mesmo arquivo com as escolhas feitas, mantendo a prévia na tela. */
  const revalidar = async (novas: Record<string, Escolha>) => {
    if (!arquivo) return;
    setValidando(true);
    try {
      setPrevia(await enviar(arquivo, false, resolucoesCompletas(novas)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao validar a planilha", { duration: 10_000 });
    } finally {
      setValidando(false);
    }
  };

  const escolher = (chave: string, escolha: Escolha) => {
    const novas = { ...escolhas, [chave]: escolha };
    setEscolhas(novas);
    // Só muda a prévia quando a escolha fica completa, ou deixa de ser.
    if (completa(escolha) || completa(escolhas[chave])) void revalidar(novas);
  };

  const confirmar = async () => {
    if (!arquivo) return;
    setImportando(true);
    try {
      const res = await enviar(arquivo, true, resolucoesCompletas(escolhas));
      const partes = [
        res.resumo.criar > 0 && `${res.resumo.criar} criado(s)`,
        res.resumo.atualizar > 0 && `${res.resumo.atualizar} atualizado(s)`,
      ].filter(Boolean);
      toast.success(`Importação concluída: ${partes.join(" e ")}`);
      if (res.resumo.erro > 0) toast.warning(`${res.resumo.erro} linha(s) com erro foram ignoradas`);
      onSuccess();
      setImportando(false);
      handleClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao importar usuários", { duration: 10_000 });
      setImportando(false);
    }
  };

  const aplicaveis = previa ? previa.resumo.criar + previa.resumo.atualizar : 0;
  const pendentes = previa?.departamentosNaoCadastrados ?? [];
  const faltaResolver = pendentes.filter((d) => !completa(escolhas[d.chave])).length;
  const acoes = previa?.acoesDepartamento;
  const bloqueado = validando || importando;
  const linhasOrdenadas = previa
    ? [...previa.linhas].sort((a, b) => ORDEM.indexOf(a.acao) - ORDEM.indexOf(b.acao) || a.linha - b.linha)
    : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar usuários</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1.5">
              <p>
                Preencha a planilha modelo e envie aqui. Quem já tem cadastro (mesmo CPF ou email) é{" "}
                <strong>atualizado</strong>; quem não tem é <strong>criado</strong> como colaborador. O email
                só é obrigatório para cadastro novo — achado pelo CPF, um email preenchido substitui o atual.
              </p>
              <p>
                Células opcionais em branco mantêm o valor atual. Perfil e status ativo/inativo não são
                alterados pela importação.
              </p>
              <p>
                A data de admissão só é importada para quem já está cadastrado como <strong>CLT</strong>.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <a
            href="/modelo-importacao-usuarios.xlsx"
            download
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <Download className="h-4 w-4" />
            Baixar planilha modelo
          </a>

          <label
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 text-sm transition-colors
              ${arquivo ? "border-primary/40 bg-primary/5" : "border-input bg-muted/30 hover:border-primary/50 hover:bg-primary/5"}`}
          >
            {validando && !previa ? (
              <>
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <span className="font-medium">Validando planilha...</span>
              </>
            ) : arquivo ? (
              <>
                <FileCheck2 className="h-7 w-7 text-primary" />
                <span className="font-medium text-primary">{arquivo.name}</span>
                <span className="text-xs text-muted-foreground">Clique para trocar</span>
              </>
            ) : (
              <>
                <Upload className="h-7 w-7 text-muted-foreground" />
                <span className="font-medium">Clique para selecionar a planilha</span>
                <span className="text-xs text-muted-foreground">.xlsx, .xls ou .csv</span>
              </>
            )}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              disabled={bloqueado}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void validar(f);
                e.target.value = "";
              }}
            />
          </label>

          {previa && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                {ORDEM.filter((a) => previa.resumo[a] > 0).map((a) => (
                  <Badge key={a} variant="outline" className={ACAO[a].className}>
                    {previa.resumo[a]} {ACAO[a].rotulo.toLowerCase()}
                  </Badge>
                ))}
                {validando && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              </div>

              {pendentes.length > 0 && (
                <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                  <p className="text-sm font-medium">
                    Departamentos que não existem no cadastro ({pendentes.length})
                  </p>
                  {pendentes.map((d) => {
                    const e = escolhas[d.chave];
                    return (
                      <div key={d.chave} className="space-y-2 rounded-md border bg-background p-2.5 text-sm">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-medium">"{d.nome}"</span>
                          <span className="text-xs text-muted-foreground">
                            {d.linhas.length > 1 ? "linhas" : "linha"} {d.linhas.join(", ")}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant={e?.acao === "vincular" ? "default" : "outline"}
                            disabled={bloqueado}
                            onClick={() => escolher(d.chave, { acao: "vincular" })}
                          >
                            Vincular a um departamento
                          </Button>
                          <Button
                            size="sm"
                            variant={e?.acao === "criar" ? "default" : "outline"}
                            disabled={bloqueado}
                            onClick={() => escolher(d.chave, { acao: "criar" })}
                          >
                            Criar novo departamento
                          </Button>
                        </div>

                        {e?.acao === "vincular" && (
                          <div className="space-y-2">
                            <Select
                              value={e.departamento ?? ""}
                              onValueChange={(v) => escolher(d.chave, { acao: "vincular", departamento: v })}
                              disabled={bloqueado}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Escolha o departamento existente" />
                              </SelectTrigger>
                              <SelectContent>
                                {departamentos.map((dep) => (
                                  <SelectItem key={dep.id} value={dep.name}>
                                    {dep.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {e.departamento && (
                              <div className="space-y-1.5">
                                <p className="text-xs">
                                  Alterar o nome do departamento <strong>"{e.departamento}"</strong> para{" "}
                                  <strong>"{d.nome}"</strong>?
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    size="sm"
                                    variant={e.renomear === true ? "default" : "outline"}
                                    disabled={bloqueado}
                                    onClick={() => escolher(d.chave, { ...e, renomear: true })}
                                  >
                                    Sim, renomear
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={e.renomear === false ? "default" : "outline"}
                                    disabled={bloqueado}
                                    onClick={() => escolher(d.chave, { ...e, renomear: false })}
                                  >
                                    Não, manter "{e.departamento}"
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {d.erro && <p className="text-xs text-destructive">{d.erro}</p>}
                      </div>
                    );
                  })}

                  {acoes && (acoes.criar.length > 0 || acoes.renomear.length > 0) && (
                    <ul className="space-y-0.5 text-xs text-muted-foreground">
                      {acoes.criar.map((n) => (
                        <li key={n}>Vai criar o departamento "{n}".</li>
                      ))}
                      {acoes.renomear.map((r) => (
                        <li key={r.de}>
                          Vai renomear "{r.de}" para "{r.para}"
                          {r.usuarios > 0 &&
                            ` — ${r.usuarios} usuário(s) já cadastrado(s) nele passa(m) para o novo nome`}
                          .
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {previa.resumo.erro > 0 && (
                <div className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Linhas com erro serão ignoradas. Corrija a planilha e envie de novo, ou importe só as
                    linhas válidas.
                  </span>
                </div>
              )}

              <div className="max-h-[45vh] divide-y overflow-y-auto rounded-md border">
                {linhasOrdenadas.map((l) => (
                  <div key={l.linha} className="flex gap-3 px-3 py-2 text-sm">
                    <span className="w-12 shrink-0 pt-0.5 font-mono text-xs text-muted-foreground">
                      L{l.linha}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{l.nome || "(sem nome)"}</span>
                        <Badge variant="outline" className={`shrink-0 text-[10px] ${ACAO[l.acao].className}`}>
                          {ACAO[l.acao].rotulo}
                        </Badge>
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{l.email || "—"}</div>
                      {l.erros.length > 0 && (
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-destructive">
                          {l.erros.map((e) => <li key={e}>{e}</li>)}
                        </ul>
                      )}
                      {l.alteracoes.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                          {l.alteracoes.map((a) => <li key={a}>{a}</li>)}
                        </ul>
                      )}
                      {l.avisos?.length > 0 && (
                        <ul className="mt-1 space-y-0.5 text-xs text-amber-700 dark:text-amber-400">
                          {l.avisos.map((a) => <li key={a}>{a}</li>)}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            {faltaResolver > 0 && (
              <span className="mr-auto text-xs text-amber-700 dark:text-amber-400">
                Resolva {faltaResolver} departamento(s) antes de importar.
              </span>
            )}
            <Button variant="outline" onClick={handleClose} disabled={importando}>
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmar()}
              disabled={!previa || aplicaveis === 0 || bloqueado || faltaResolver > 0}
            >
              {importando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {previa && aplicaveis > 0 ? `Importar ${aplicaveis} usuário(s)` : "Importar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
